// Who is signed in, which company they work in, and what their role lets them open.
// Reads go through RLS (x-tenant-id = the active company); writes through database functions.
import type { Session } from '@supabase/supabase-js';
import { createStore, useStoreOf } from './store';
import { DbError, rpc, sb, setTenantHeader } from './supabase';

export type Role = 'owner' | 'admin' | 'finance' | 'supervisor' | 'collector' | 'auditor';
export type Plan = 'free' | 'solo' | 'team' | 'business' | 'enterprise';

export interface Workspace {
  tenant_id: string; kind: 'company' | 'personal'; name_en: string; name_ar: string | null; short: string | null;
  plan: Plan; status: 'active' | 'past_due' | 'locked' | 'closed'; role: Role; membership_status: 'invited' | 'active'; brand_color: string | null;
}
export interface Company {
  id: string; name_en: string; name_ar: string | null; short: string | null; trn: string | null; plan: Plan;
  status: Workspace['status']; currency: string; created_by: string | null; created_at: string;
}
export interface Setup { company: boolean; brand: boolean; look: boolean; rules: boolean; customers: boolean; people: boolean; billing: boolean; done: number; total: number; hidden: boolean }
export interface Me { id: string; email: string; name: string; role: Role; seat: 'issuer' | 'office'; membershipId: string }

export type AuthStep = { step: 'loading' } | { step: 'start'; err?: string } | { step: 'password'; email: string; err?: string } | { step: 'code'; email: string; err?: string } | { step: 'mfa'; factorId: string; err?: string } | { step: 'choose' } | { step: 'none' };

interface State {
  auth: AuthStep;
  session: Session | null;
  workspaces: Workspace[];
  co: Company | null;
  brandColor: string;
  me: Me | null;
  setup: Setup | null;
  receipts: number;
  /** xReceived staff role (staff_users), or null: staff see the Staff pages (Testers). */
  staff: string | null;
}
export const app = createStore<State>({ auth: { step: 'loading' }, session: null, workspaces: [], co: null, brandColor: '#0b5d4f', me: null, setup: null, receipts: 0, staff: null });
export const useApp = () => useStoreOf(app);

const LAST = 'xr-admin-company';
const remember = (id: string | null) => { try { if (id) localStorage.setItem(LAST, id); else localStorage.removeItem(LAST); } catch { /* storage blocked */ } };
const lastCompany = () => { try { return localStorage.getItem(LAST); } catch { return null; } };

/* ---- Capabilities: the prototype's can() for the parts built so far ---- */
const r = () => app.get().me?.role;
const locked = () => app.get().co?.status === 'locked';
export function can(cap: 'web' | 'seeMoney' | 'seeAll' | 'issue' | 'customers' | 'seeAdmin' | 'admin' | 'billing' | 'audit' | 'reports' | 'ai'): boolean {
  const role = r();
  if (!role) return false;
  const ro = role === 'auditor' || locked();
  switch (cap) {
    case 'web': return role !== 'collector';
    case 'seeMoney': case 'reports': return ['owner', 'admin', 'finance', 'auditor', 'supervisor'].includes(role);
    case 'seeAll': return ['owner', 'admin', 'finance', 'auditor'].includes(role);
    case 'issue': return !ro && ['owner', 'admin', 'finance'].includes(role) && app.get().me?.seat === 'issuer';
    case 'customers': return !ro && ['owner', 'admin', 'finance'].includes(role);
    case 'seeAdmin': return ['owner', 'admin', 'auditor'].includes(role);
    case 'admin': return !ro && ['owner', 'admin'].includes(role);
    case 'billing': return role === 'owner';
    case 'audit': return ['owner', 'admin', 'auditor'].includes(role);
    case 'ai': return role !== 'auditor';
  }
}

/* ---- Sign-in ---- */
let booted = false;
export async function boot() {
  if (booted) return; // StrictMode runs effects twice in development
  booted = true;
  // signed out elsewhere, or the session ran out: back to sign-in
  sb.auth.onAuthStateChange(ev => { if (ev === 'SIGNED_OUT' && app.get().session) signedOut(); });
  try {
    const { data } = await sb.auth.getSession();
    if (!data.session) return app.set({ auth: { step: 'start' }, session: null });
    await afterAuth(data.session);
  } catch {
    app.set({ auth: { step: 'start', err: 'network' }, session: null, co: null, me: null });
  }
}

export async function sendCode(email: string) {
  const { error } = await sb.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
  if (error) throw new DbError(/signups not allowed|user not found/i.test(error.message) ? 'no_account' : error.status === 429 ? 'too_many' : 'network', error.message);
}

export async function signInPassword(email: string, password: string) {
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error || !data.session) throw new DbError(error?.status === 429 ? 'too_many' : error?.status === 400 || error?.status === 401 ? 'wrong_password' : 'network', error?.message);
  await afterAuth(data.session);
}

export async function verifyCode(email: string, token: string) {
  const { data, error } = await sb.auth.verifyOtp({ email, token, type: 'email' });
  if (error || !data.session) throw new DbError(error?.status === 429 ? 'too_many' : 'wrong_code', error?.message);
  await afterAuth(data.session);
}

export async function google() {
  const { error } = await sb.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: location.origin + location.pathname } });
  if (error) throw new DbError('network', error.message);
}

export async function verifyMfa(factorId: string, code: string) {
  const { error } = await sb.auth.mfa.challengeAndVerify({ factorId, code });
  if (error) throw new DbError('wrong_code', error.message);
  const { data } = await sb.auth.getSession();
  if (data.session) await afterAuth(data.session);
}

async function afterAuth(session: Session) {
  app.set({ session });
  const aal = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aal.data && aal.data.currentLevel === 'aal1' && aal.data.nextLevel === 'aal2') {
    const f = await sb.auth.mfa.listFactors();
    const totp = f.data?.totp.find(x => x.status === 'verified');
    if (totp) return app.set({ auth: { step: 'mfa', factorId: totp.id } });
  }
  setTenantHeader(null);
  const staff = await rpc<string | null>('my_staff_role').catch(() => null);
  app.set({ staff });
  const ws = (await rpc<Workspace[]>('my_workspaces')).filter(w => w.kind === 'company' && w.membership_status === 'active');
  app.set({ workspaces: ws });
  if (!ws.length) return app.set({ auth: { step: 'none' } });
  const last = lastCompany();
  const pick = ws.find(w => w.tenant_id === last) || (ws.length === 1 ? ws[0] : null);
  if (pick) return openCompany(pick.tenant_id);
  app.set({ auth: { step: 'choose' } });
}

export async function openCompany(id: string) {
  const w = app.get().workspaces.find(x => x.tenant_id === id);
  if (!w) return;
  setTenantHeader(id);
  remember(id);
  const uid = app.get().session!.user.id;
  const [co, mem, prof] = await Promise.all([
    sb.from('tenants').select('id,name_en,name_ar,short,trn,plan,status,currency,created_by,created_at').eq('id', id).single(),
    sb.from('memberships').select('id,role,seat_type,work_email').eq('tenant_id', id).eq('user_id', uid).single(),
    sb.from('user_profiles').select('name').eq('user_id', uid).maybeSingle(),
  ]);
  if (co.error || mem.error) throw new DbError('network', (co.error || mem.error)?.message);
  const email = app.get().session!.user.email || '';
  const me: Me = { id: uid, email, name: prof.data?.name || email.split('@')[0] || '', role: mem.data.role, seat: mem.data.seat_type, membershipId: mem.data.id };
  app.set({ co: co.data as Company, me, brandColor: w.brand_color || '#0b5d4f', auth: { step: 'none' }, setup: null });
  await refreshSetup();
}

export async function refreshSetup() {
  if (!can('web')) return;
  const [setup, rc] = await Promise.all([
    rpc<Setup>('setup_status'),
    sb.from('receipts').select('id', { count: 'exact' }).neq('status', 'draft').limit(1),
  ]);
  app.set({ setup, receipts: rc.count || 0 });
}

export async function setSetupHidden(hidden: boolean) {
  await rpc('set_setup_hidden', { p_hidden: hidden });
  await refreshSetup();
}

export async function signOut() {
  await sb.auth.signOut().catch(() => undefined);
  signedOut();
}
function signedOut() {
  setTenantHeader(null);
  app.set({ auth: { step: 'start' }, session: null, co: null, me: null, setup: null, workspaces: [], staff: null });
}
